import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, CheckCircle2, Clock, ChevronRight, Loader2, RefreshCw, BookOpen, GraduationCap, MoreVertical, Archive, Trash2, AlertTriangle, Loader,
} from "lucide-react";
import { toast } from "sonner";
import { AddClassDialog } from "@/components/AddClassDialog";
import { TeacherShell } from "@/components/TeacherShell";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

// Carte d'une classe : nom, niveau, nb d'élèves, signés / en attente + menu d'actions.
function ClassCard({ cls, index, onOpen, onArchive, onDelete }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05, ease: "easeOut" }}
      className="group relative flex w-full flex-col gap-4 rounded-2xl border border-slate-200/70 bg-card p-6 text-left shadow-[0_2px_10px_-3px_rgba(30,58,138,0.06)] transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-[0_12px_30px_-12px_rgba(30,58,138,0.18)] dark:border-white/10 dark:bg-card"
      data-testid={`class-card-${cls.id}`}
    >
      <div className="absolute right-3 top-3 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              data-testid={`class-menu-${cls.id}`}
              className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/5"
              aria-label="Actions de la classe"
            >
              <MoreVertical size={18} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => onOpen(cls.id)} data-testid={`menu-open-${cls.id}`}>
              <ChevronRight size={15} className="mr-2" /> Ouvrir la classe
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onArchive(cls)} data-testid={`menu-archive-${cls.id}`}>
              <Archive size={15} className="mr-2" /> Archiver la classe
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onDelete(cls)} className="text-rose-600 focus:text-rose-600" data-testid={`menu-delete-${cls.id}`}>
              <Trash2 size={15} className="mr-2" /> Supprimer définitivement
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <button type="button" onClick={() => onOpen(cls.id)} className="flex flex-col gap-4 text-left" data-testid={`class-open-${cls.id}`}>
        <div className="flex items-start justify-between pr-8">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary text-blue-900 dark:bg-blue-500/15 dark:text-blue-300">
              <GraduationCap size={20} strokeWidth={1.9} />
            </span>
            <div>
              <h3 className="font-heading text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">{cls.name}</h3>
              {cls.level && <span className="mt-0.5 inline-block text-xs font-medium text-slate-400">{cls.level}</span>}
            </div>
          </div>
          <ChevronRight className="text-slate-300 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-blue-500" />
        </div>

        <div className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
          <Users size={16} strokeWidth={1.9} />
          <span data-testid={`class-students-${cls.id}`}>{cls.student_count} élève{cls.student_count > 1 ? "s" : ""}</span>
        </div>

        <div className="flex gap-2">
          <span className="flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-800 dark:bg-blue-500/10 dark:text-blue-300" data-testid={`class-signed-${cls.id}`}>
            <CheckCircle2 size={15} /> {cls.signed_count} signé{cls.signed_count > 1 ? "s" : ""}
          </span>
          <span className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-500 dark:bg-white/5 dark:text-slate-400" data-testid={`class-unsigned-${cls.id}`}>
            <Clock size={15} /> {cls.unsigned_count} en attente
          </span>
        </div>
      </button>
    </motion.div>
  );
}

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/classes");
      setData(res.data);
    } catch {
      toast.error("Impossible de charger les classes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doArchive = async () => {
    if (!archiveTarget) return;
    setBusy(true);
    try {
      await api.post(`/classes/${archiveTarget.id}/archive`);
      toast.success("Classe archivée. Retrouvez-la dans « Classes archivées ».");
      setArchiveTarget(null);
      load();
    } catch {
      toast.error("Archivage impossible.");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.delete(`/classes/${deleteTarget.id}`);
      toast.success("Classe déplacée vers la corbeille (restaurable 30 jours).");
      setDeleteTarget(null);
      setConfirmName("");
      load();
    } catch {
      toast.error("Suppression impossible.");
    } finally {
      setBusy(false);
    }
  };

  const openClass = (id) => navigate(`/enseignant/classe/${id}`);
  const classes = data?.classes || [];
  const totalStudents = classes.reduce((a, c) => a + c.student_count, 0);
  const totalSigned = classes.reduce((a, c) => a + c.signed_count, 0);
  const totalPending = classes.reduce((a, c) => a + c.unsigned_count, 0);
  const totalPassports = totalSigned + totalPending;
  const signedPct = totalPassports ? Math.round((totalSigned / totalPassports) * 100) : 0;

  return (
    <TeacherShell active="dashboard">
      {/* Zone de bienvenue */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl dark:text-white">
            Bonjour {user?.first_name} <span className="inline-block">👋</span>
          </h1>
          <p className="mt-2 text-base text-slate-500 sm:text-lg dark:text-slate-400">
            Voici ce qui se passe dans votre classe cette semaine.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => navigate("/enseignant/rentree")} data-testid="rentree-menu-button"
            className="flex items-center gap-2 rounded-full border border-slate-200 bg-card px-4 py-2.5 text-sm font-medium text-slate-700 transition-transform duration-200 hover:scale-[0.98] active:scale-95 dark:border-white/10 dark:text-slate-200">
            <RefreshCw size={16} /> Assistant de rentrée
          </button>
        </div>
      </motion.div>

      {loading ? (
        <div className="flex justify-center py-24" data-testid="dashboard-loading">
          <Loader2 className="animate-spin text-slate-400" size={28} />
        </div>
      ) : classes.length ? (
        <div className="space-y-8">
          {/* Passeport de la semaine — mis en avant */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}
            className="relative overflow-hidden rounded-3xl bg-blue-950 p-7 text-white shadow-[0_20px_50px_-20px_rgba(30,58,138,0.5)] sm:p-9" data-testid="week-highlight">
            <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-blue-500/20 blur-3xl" />
            <div aria-hidden className="pointer-events-none absolute -bottom-20 right-24 h-40 w-40 rounded-full bg-blue-400/10 blur-3xl" />
            <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="max-w-md">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-blue-100">
                  <BookOpen size={13} /> Le Passeport de la semaine
                </span>
                <p className="mt-4 font-heading text-2xl font-semibold tracking-tight sm:text-3xl">
                  {totalSigned} / {totalPassports} passeports signés
                </p>
                <p className="mt-1.5 text-sm text-blue-200">
                  {totalPending > 0
                    ? `${totalPending} famille${totalPending > 1 ? "s" : ""} n'ont pas encore signé.`
                    : "Toutes les familles ont signé. Bravo !"}
                </p>
                <div className="mt-5 h-2 w-full max-w-xs overflow-hidden rounded-full bg-white/15">
                  <div className="h-full rounded-full bg-blue-300 transition-all duration-500" style={{ width: `${signedPct}%` }} />
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-6">
                <div className="text-center">
                  <p className="font-heading text-3xl font-semibold">{classes.length}</p>
                  <p className="text-xs text-blue-200">classe{classes.length > 1 ? "s" : ""}</p>
                </div>
                <div className="h-10 w-px bg-white/15" />
                <div className="text-center">
                  <p className="font-heading text-3xl font-semibold">{totalStudents}</p>
                  <p className="text-xs text-blue-200">élèves</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Classes */}
          <div id="classes" className="scroll-mt-6">
            <div className="mb-4 flex items-end justify-between">
              <div>
                <h2 className="font-heading text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Vos classes</h2>
                {data?.school_year && <p className="text-sm text-slate-400">Année scolaire {data.school_year}</p>}
              </div>
              <div className="w-auto"><AddClassDialog onCreated={load} /></div>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="classes-grid">
              {classes.map((cls, i) => (
                <ClassCard
                  key={cls.id} cls={cls} index={i} onOpen={openClass}
                  onArchive={setArchiveTarget}
                  onDelete={(c) => { setConfirmName(""); setDeleteTarget(c); }}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="flex flex-col items-center gap-5 rounded-3xl border border-dashed border-slate-200 bg-card/60 py-24 text-center dark:border-white/10" data-testid="empty-state">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-secondary text-blue-900 dark:bg-blue-500/10 dark:text-blue-300">
            <GraduationCap size={40} strokeWidth={1} />
          </span>
          <div className="max-w-sm">
            <h3 className="font-heading text-xl font-semibold text-slate-900 dark:text-slate-100">Votre première classe n'est pas encore créée</h3>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              C'est la rentrée ? Configurez vos classes, importez vos élèves et générez les identifiants des familles en quelques minutes.
            </p>
          </div>
          <button onClick={() => navigate("/enseignant/rentree")} data-testid="empty-rentree-button"
            className="flex items-center gap-2 rounded-full bg-blue-900 px-5 py-3 text-sm font-medium text-white shadow-lg shadow-blue-900/20 transition-transform duration-200 hover:scale-[0.98] active:scale-95 dark:bg-blue-500">
            <RefreshCw size={16} /> Lancer l'assistant de rentrée
          </button>
        </motion.div>
      )}

      {/* Confirmation d'archivage */}
      <Dialog open={!!archiveTarget} onOpenChange={(v) => !v && setArchiveTarget(null)}>
        <DialogContent className="rounded-2xl sm:max-w-[440px]" data-testid="archive-class-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Archive size={18} className="text-amber-500" /> Archiver « {archiveTarget?.name} » ?</DialogTitle>
            <DialogDescription>
              La classe ne sera plus affichée parmi vos classes actives, mais toutes ses informations (élèves, passeports, signatures, système d'évaluation) seront conservées et resteront accessibles dans « Classes archivées ».
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setArchiveTarget(null)}>Annuler</Button>
            <Button className="rounded-xl bg-amber-500 text-white hover:bg-amber-600" onClick={doArchive} disabled={busy} data-testid="confirm-archive-class">
              {busy ? <Loader size={16} className="animate-spin" /> : "Archiver la classe"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suppression (confirmation renforcée -> corbeille) */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) { setDeleteTarget(null); setConfirmName(""); } }}>
        <DialogContent className="rounded-2xl sm:max-w-[460px]" data-testid="delete-class-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700 dark:text-rose-400">
              <AlertTriangle size={20} /> Supprimer définitivement « {deleteTarget?.name} » ?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-slate-500 dark:text-slate-400">
                <p>Attention : cette action supprimera la classe et toutes ses données associées.</p>
                <p>La classe sera déplacée dans la <strong>Corbeille</strong> et pourra être restaurée pendant <strong>30 jours</strong>, après quoi la suppression deviendra définitive.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <label className="text-sm text-slate-600 dark:text-slate-300">
              Pour confirmer, saisissez le nom exact de la classe : <strong>{deleteTarget?.name}</strong>
            </label>
            <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={deleteTarget?.name} data-testid="delete-class-confirm-input" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => { setDeleteTarget(null); setConfirmName(""); }} data-testid="cancel-delete-class">Annuler</Button>
            <Button
              className="rounded-xl bg-rose-600 text-white hover:bg-rose-700"
              disabled={busy || confirmName.trim() !== (deleteTarget?.name || "")}
              onClick={doDelete}
              data-testid="confirm-delete-class"
            >
              {busy ? <Loader size={16} className="animate-spin" /> : "Supprimer définitivement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TeacherShell>
  );
}
